import { Pricing } from "./pricing";
import { get, getJSON } from "./RequestHelper";
import { Accessory } from "./components/AccessoryDisplay";
import { APIResponse } from "./components/common/APIResponse";
import { CordType, DischargeType, ImpellerMaterial, LEPPumpViewModel, LowerSealMaterial, PumpProperty } from "./components/common/LEPPumpViewModel";

const getBridgeAPIBaseURL = (): string => {
    return window.buildAPumpSettings?.apiUrl ?? 'https://lp-bridgeapi.azurewebsites.net/api/v2/';
}

/**
 * Get all options available to Single Seal models in the view model.
 * Single Seal options have always been hard-coded, I'd like to get these from Bridge API one day...
 * @param model - the LEP Pump model that is currently configured
 */
export const populateSingleSealPumpOptions = (model: LEPPumpViewModel) : void => {
    model.frequency = null;
    // grinder pump options
    if (model.pumpType === 'LGV' ||
        model.pumpType === 'LGH' || 
        model.pumpType === 'XLGV' ||
        model.pumpType === 'XLGH') {
          model.voltageOptions = [
            {
              Code: '8', Description: '200 1Ø'
            },
            {
              Code: '2', Description: '230 1Ø'
            },
            {
              Code: '3', Description: '200/230 3Ø'
            },
            {
              Code: '4', Description: '460 3Ø'
            },
            {
              Code: '5', Description: '575 3Ø'
            }
          ];
          model.operationType = 'M';
          model.operationTypeDesc = 'Manual';
          model.cordLengthOptions = [
            {
              Code: '-2', Description: '25\'', Standard: true
            },
            {
              Code: '-3', Description: '35\''
            },
            {
              Code: '-5', Description: '50\''
            }
          ];
          model.cordLength = '-2';
          return;
        }
        // solids handling
        const solidsHandlingSizeOptions: PumpProperty[] = [];
        model.voltageOptions = [
          {Code: '2', Description: '200/230 1Ø'},
          {Code: '3', Description: '200/230 3Ø'},
          {Code: '4', Description: '460 3Ø'},
          {Code: '5', Description: '575 3Ø'}
        ];

        if (model.pumpType === 'LE') {
          model.pumpTypeDesc = 'Open 2 Vane Ordinary Location';
          model.solidsHandlingSize = 2;
          solidsHandlingSizeOptions.push({Code: '2', Description: '2.5"'});
        } else {
          // model.pumpType is LEV
          model.pumpTypeDesc = 'Vortex Ordinary Location';
          model.solidsHandlingSize = 3;
          solidsHandlingSizeOptions.push({Code: '3', Description: '3"'});
        }

        model.solidsHandlingSizeOptions = solidsHandlingSizeOptions;
        model.impellerMaterialOptions = [
          {Code: 'M', Description: 'Cast Iron', Standard: true},
          {Code: 'BM', Description: 'Bronze'}
        ];
        model.impellerMaterial = ImpellerMaterial.M;
        model.cordLengthOptions = [
          {Code: '-3', Description: '35\'', Standard: true},
          {Code: '-5', Description: '50\''},
          {Code: '-1', Description: '100\''}
        ];
        model.cordLength = '-3';
        model.capacitorOptions = [
          {Code: '', Description: 'Internal Capacitor', Standard: true},
          {Code: 'C', Description: 'External Capacitor'}
        ];
        model.capacitor = false;

        model.dischargeOrientationOptions = [
            {Code: '', Description: 'Horizontal Discharge', Standard: true},
            {Code: 'V', Description: 'Vertical Discharge'}
        ];
        model.verticalDischarge = false;
}

/**
 * Get all available options for each configurable option on a Dual Seal pump.
 * @param model - the LEP Pump model that is currently configured
 */
export const populateDualSealPumpOptions = async (model: LEPPumpViewModel) : Promise<void> => {
    const endpoint: string = `BAP/GetAddonValuesWithFrequency?pumpType=${model.pumpType}&horsepower=${model.horsepower}&frequency=${model.frequency}`;
    const response: Promise<APIResponse> = getJSON(`${getBridgeAPIBaseURL()}${endpoint}`);
    await response.then(r => {
      model.solidsHandlingSizeOptions = r.SolidsHandling;
      const defaultSolidsSize: PumpProperty | undefined = r.SolidsHandling.find(o => o.Standard);
      if (defaultSolidsSize) {
        model.solidsHandlingSize = parseInt(defaultSolidsSize.Code ? defaultSolidsSize.Code : '');
      }
      model.voltageOptions = r.Voltage;
      model.frequencyOptions = r.Frequency;
      model.impellerSizeOptions = r.ImpellerSize;
      const defaultImpellerSize: PumpProperty | undefined = r.ImpellerSize.find(o => o.Standard);
      if (defaultImpellerSize) {
        model.impellerSize = defaultImpellerSize.Code;
      }
      model.impellerMaterialOptions = r.ImpellerMaterial;
      const defaultImpellerMaterial: PumpProperty | undefined = r.ImpellerMaterial.find(o => o.Standard);
      if (defaultImpellerMaterial) {
        model.impellerMaterial = defaultImpellerMaterial.Code as Nullable<ImpellerMaterial>;
      }
      model.sealConfigOptions = r.SealConfiguration;
      const defaultSealConfig: PumpProperty | undefined = r.SealConfiguration.find(o => o.Standard);
      if (defaultSealConfig) {
        model.sealConfig = defaultSealConfig.Code;
      }
      model.lowerSealMaterialOptions = r.LowerSealMaterial;
      const defaultLowerSealMaterial: PumpProperty | undefined = r.LowerSealMaterial.find(o => o.Standard);
      if (defaultLowerSealMaterial) {
        model.lowerSealMaterial = defaultLowerSealMaterial.Code as Nullable<LowerSealMaterial>;
      }
      model.dischargeOptions = r.Discharge;
      const defaultDischarge: PumpProperty | undefined = r.Discharge.find(o => o.Standard);
      if (defaultDischarge) {
        model.discharge = defaultDischarge.Code as Nullable<DischargeType>;
      }
      model.cordTypeOptions = r.CordType;
      const defaultCordType: PumpProperty | undefined = r.CordType.find(o => o.Standard);
      if (defaultCordType) {
        model.cordType = defaultCordType.Code as Nullable<CordType>;
      }
      model.cordLengthOptions = r.PowerCordLength;
      const defaultCordLength: PumpProperty | undefined = r.PowerCordLength.find(o => o.Standard);
      if (defaultCordLength) {
        model.cordLength = defaultCordLength.Code;
      }
    });
}

/**
 * Get horsepower values available to Grinder pumps
 * @param hazardousLocation - is the configured pump an explosion-proof pump?
 * @returns JSON containing available horsepower values
 */
export const getGrinderHPValues = async (hazardousLocation: boolean) : Promise<PumpProperty[]> => {
    return getHPValues(`BAP/GetGrinderHorsepower?ordinaryLocation=${!hazardousLocation}`);
}

/**
 * Get horsepower values available to Solids Handling pumps
 * @returns JSON containing available horsepower values
 */
export const getSolidsHandlingHPValues = async () : Promise<PumpProperty[]> => {
    return getHPValues('BAP/GetSolidsHP?impellerType=LE&solidsSize=3');
}

const getHPValues = async (endpoint: string): Promise<PumpProperty[]> => {
    return getJSON(`${getBridgeAPIBaseURL()}${endpoint}`);
}

/**
 * Get the list price for a given model number.
 * @param modelNumber - the model number to search by
 * @param CAD - should the price be in Canadian dollars?
 * @returns a string containing the list price
 */
export const getListPrice = async (modelNumber: string, CAD: boolean) : Promise<string> => {
    if (modelNumber === '') {
        return '';
    }
    const endpoint: string = `BAP/GetListPrice?modelNum=${modelNumber}&priceType=${CAD ? 'CAD' : 'USD'}`;
    let result: string = '';
    const response: Promise<any> = get(`${getBridgeAPIBaseURL()}${endpoint}`);
    await response.then(r => r.text()).then(t => result = t);
    return result;
}

const getSSAControlPanels = async (model: LEPPumpViewModel) : Promise<Accessory[]> => {
    const endpoint: string = `BAPConfig/GetSingleSealControlPanels?pump=${model.getModelNumber()}`;
    return await getJSON<Promise<Accessory[]>>(`${getBridgeAPIBaseURL()}${endpoint}`);
}

const getDSAControlPanels = async (model: LEPPumpViewModel) : Promise<Accessory[]> => {
    const endpoint: string = `BAPConfig/GetDualSealControlPanels?pumpType=${model.pumpType}&hp=${model.horsepower}&voltage=${model.voltage}`;
    return await getJSON<Promise<Accessory[]>>(`${getBridgeAPIBaseURL()}${endpoint}`);
}

/**
 * Get all Control Panels available for the currently configured model.
 * @param model - the LEP Pump model that is currently configured
 * @returns (Promise) an array of Accessories.
 */
export const getControlPanels = async (model: LEPPumpViewModel) : Promise<Accessory[]> => {
    if (model.isSingleSeal || model.isSmallGrinder) {
        return await getSSAControlPanels(model);
    }
    return await getDSAControlPanels(model);
}

/**
 * Get all Capacitor Kits available for the currently configured model.
 * @param model the LEP Pump model that is currently configured
 * @returns (Promise) an array of Accessories.
 */
export const getSingleSealCapKits = async (model: LEPPumpViewModel) : Promise<Accessory[]> => {
    const endpoint: string = `BAPConfig/GetSingleSealCapKits?pump=${model.getModelNumber()}`;
    return await getJSON<Promise<Accessory[]>>(`${getBridgeAPIBaseURL()}${endpoint}`);
}

/**
 * Get all Capacitor Kits available for the currently configured model.
 * NOTE: Use this method to get Cap Kits when the control panel choice was made
 * @param model the LEP Pump model that is currently configured
 * @returns (Promise) an array of Accessories.
 */
export const getDualSealCapKitsForControlPanel = async (model?: LEPPumpViewModel) : Promise<Accessory[]> => {
    const solidsBit: boolean = model?.solidsHandlingSize !== undefined && model?.solidsHandlingSize !== null;
    const endpoint: string = `BAPConfig/GetDualSealCapKits?voltage=${model?.voltage}&solidsHandling=${solidsBit}`;
    return await getJSON<Promise<Accessory[]>>(`${getBridgeAPIBaseURL()}${endpoint}`);
}

/**
 * Get all Capacitor Kits available for the currently configured model.
 * NOTE: Use this method to get Cap Kits when the control panel choice was skipped
 * @param model the LEP Pump model that is currently configured
 * @returns (Promise) an array of Accessories.
 */
export const getDualSealCapKitsNoControlPanel = async (model?: LEPPumpViewModel) : Promise<Accessory[]> => {
    return await getJSON<Promise<Accessory[]>>(`${getBridgeAPIBaseURL()}BAPConfig/GetSinglePumpCapKits`);
}

/**
 * Get all Floats available for the chosen control panel.
 * @param controlPanel the control panel picked by the user in the Accessory Display
 * @returns (Promise) an array of Accessories.
 */
export const getDSAFloats = async (controlPanel: string) : Promise<Accessory[]> => {
    const endpoint: string = `BAPConfig/GetFloatsByControlPanel?controlPanel=${controlPanel}`;
    return await getJSON<Promise<Accessory[]>>(`${getBridgeAPIBaseURL()}${endpoint}`);
}

const getSSAGuideRails = async (model: LEPPumpViewModel) : Promise<Accessory[]> => {
    const endpoint: string = `BAPConfig/GetSingleSealGuideRails?pump=${model.getModelNumber()}`;
    return await getJSON<Promise<Accessory[]>>(`${getBridgeAPIBaseURL()}${endpoint}`);
}

const getDSAGuideRails = async (model: LEPPumpViewModel) : Promise<Accessory[]> => {
    const endpoint: string = `BAPConfig/GetDualSealGuideRails?pumpType=${model.pumpType}`;
    return await getJSON<Promise<Accessory[]>>(`${getBridgeAPIBaseURL()}${endpoint}`);
}

/**
 * Get all Guide Rails available for the currently configured model.
 * @param model the LEP Pump model that is currently configured
 * @returns (Promise) an array of Accessories.
 */
export const getGuideRails = async (model: LEPPumpViewModel) : Promise<Accessory[]> => {
    if (model.isSingleSeal || model.isSmallGrinder) {
        return await getSSAGuideRails(model);
    }
    return await getDSAGuideRails(model);
}

/**
 * Get a PDF of all literature related to this configured model and chosen accessories.
 * @param model the LEP Pump model that is currently configured
 * @param controlPanel part number of the control panel selected
 * @param capKit part number of the capacitor kit selected
 * @param float part number of the float selected
 * @param guideRail part number of the guide rail selected
 * @param pricing pricing level being shown to this user
 */
export const printPacket = async (model: LEPPumpViewModel, controlPanel: string, capKit: string, float: string, guideRail: string, pricing: Pricing) : Promise<void> => {
    let method: string = '';
    let pricingStr: string = '';
    let canadianPrice: boolean = false;
    let showPricing: boolean = false;
    switch (pricing) {
        case Pricing.ALL:
        case Pricing.US:
            pricingStr = 'USD';
            showPricing = true;
            break;
        case Pricing.CA:
            pricingStr = 'CAD';
            canadianPrice = true;
            showPricing = true;
            break;
        case Pricing.NONE:
            pricingStr = '';
            break;
    }
    pricingStr = showPricing ? `${await getListPrice(model.getModelNumber(), canadianPrice)}` : '';

    // get pricing for accessories
    let controlPanelPrice: string = showPricing ? await getListPrice(controlPanel, canadianPrice) : '';
    let capKitPrice: string = showPricing ? await getListPrice(capKit, canadianPrice) : '';
    let floatPrice: string = showPricing ? await getListPrice(float, canadianPrice) : '';
    let guideRailPrice: string = showPricing ? await getListPrice(guideRail, canadianPrice) : '';

    if (model.isSingleSeal || model.isSmallGrinder) {
        method = 'BAP/PrintSSPacket';
    }
    else {
        method = 'BAP/PrintDSPacket';
    }
    const queryString : string = `${getBridgeAPIBaseURL()}${method}` +
                                 `?LEPModel=${model.getModelNumber().trim()}&series=${model.pumpType}&pricing=${pricingStr}` +
                                 `&controlPanel=${controlPanel}&remoteMonitor=&capKit=${capKit}&float=${float}&guideRail=${guideRail}` +
                                 `&cpPrice=${controlPanelPrice}&rmPrice=&ckPrice=${capKitPrice}&fPrice=${floatPrice}&grPrice=${guideRailPrice}`;
    window.open(queryString, '_blank');
}
